import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import Breadcrumb from '../components/Breadcrumb';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { 
  ArrowLeft, Shield, Loader2, Save, Plus, Trash2, Edit, 
  Globe, Search, FileText, Link2, AlertTriangle, CheckCircle,
  Code, Map, RefreshCw, ExternalLink, Copy, BarChart3,
  Settings2, Layers, Eye, XCircle, ArrowRight
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Stat Card Component
const StatCard = ({ icon: Icon, title, value, color = 'emerald' }) => {
  const colorClasses = {
    emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    blue: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    red: 'bg-red-500/10 text-red-400 border-red-500/20',
    yellow: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
    purple: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  };

  return (
    <div className={`rounded-xl border p-4 ${colorClasses[color]}`}>
      <div className="flex items-center gap-3">
        <Icon className="w-5 h-5" />
        <div>
          <p className="text-zinc-400 text-sm">{title}</p>
          <p className="text-xl font-bold text-white">{value}</p>
        </div>
      </div>
    </div>
  );
};

const SEOManagementPage = () => {
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // State
  const [globalSettings, setGlobalSettings] = useState({});
  const [templates, setTemplates] = useState([]);
  const [redirects, setRedirects] = useState([]);
  const [notFoundLogs, setNotFoundLogs] = useState([]);
  const [schemas, setSchemas] = useState([]);
  const [audit, setAudit] = useState({ issues: [], warnings: [], passed: [], stats: {} });
  const [programmaticConfig, setProgrammaticConfig] = useState({});
  const [hreflangConfig, setHreflangConfig] = useState({});
  const [rankKeywords, setRankKeywords] = useState([]);
  const [rankChecking, setRankChecking] = useState(null);
  const [rankCheckingAll, setRankCheckingAll] = useState(false);
  const [newKeyword, setNewKeyword] = useState('');
  const [sitemapConfig, setSitemapConfig] = useState({
    include_pages: true,
    include_categories: true,
    include_listings: true,
    include_blog: true,
    include_images: true,
    listings_priority: 0.7,
    blog_priority: 0.6,
    categories_priority: 0.8,
    pages_priority: 0.6
  });
  
  // Edit modals
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editingRedirect, setEditingRedirect] = useState(null);
  const [editingSchema, setEditingSchema] = useState(null);
  const [newRedirectModal, setNewRedirectModal] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchAllData();
    }
  }, [user, token]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [settingsRes, templatesRes, redirectsRes, logsRes, schemasRes, auditRes, progRes, hreflangRes, sitemapRes, rankRes] = await Promise.all([
        axios.get(`${API}/seo/settings`, { headers }),
        axios.get(`${API}/seo/templates`, { headers }),
        axios.get(`${API}/seo/redirects`, { headers }),
        axios.get(`${API}/seo/404-logs`, { headers }),
        axios.get(`${API}/seo/schemas`, { headers }),
        axios.get(`${API}/seo/audit`, { headers }),
        axios.get(`${API}/seo/programmatic-pages`, { headers }),
        axios.get(`${API}/seo/hreflang`, { headers }),
        axios.get(`${API}/seo/sitemap-config`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/seo/rank-keywords`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      setGlobalSettings(settingsRes.data);
      setTemplates(templatesRes.data);
      setRedirects(redirectsRes.data);
      setNotFoundLogs(logsRes.data);
      setSchemas(schemasRes.data);
      setAudit(auditRes.data);
      setProgrammaticConfig(progRes.data);
      setHreflangConfig(hreflangRes.data);
      if (sitemapRes.data && Object.keys(sitemapRes.data).length > 0) {
        setSitemapConfig(sitemapRes.data);
      }
      setRankKeywords(rankRes.data || []);
    } catch (error) {
      console.error('Error fetching SEO data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveGlobalSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/seo/settings`, globalSettings, { headers });
      alert(t('common.success', 'Settings saved!'));
    } catch (error) {
      alert(t('common.error', 'Failed to save settings'));
    } finally {
      setSaving(false);
    }
  };

  const saveSitemapConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/seo/sitemap-config`, sitemapConfig, { headers });
      alert(t('common.success', 'Sitemap settings saved!'));
    } catch (error) {
      alert(t('common.error', 'Failed to save sitemap settings'));
    } finally {
      setSaving(false);
    }
  };

  const createRedirect = async (data) => {
    try {
      await axios.post(`${API}/seo/redirects`, data, { headers });
      fetchAllData();
      setNewRedirectModal(false);
    } catch (error) {
      alert(error.response?.data?.detail || t('seo.redirectCreateError', 'Failed to create redirect'));
    }
  };

  const deleteRedirect = async (id) => {
    try {
      await axios.delete(`${API}/seo/redirects/${id}`, { headers });
      setRedirects(redirects.filter(r => r.id !== id));
    } catch (error) {
      alert(t('seo.redirectDeleteError', 'Failed to delete redirect'));
    }
  };

  const resolve404 = async (url, targetUrl = '') => {
    try {
      await axios.put(`${API}/seo/404-logs/${encodeURIComponent(url)}/resolve?target_url=${encodeURIComponent(targetUrl)}`, {}, { headers });
      fetchAllData();
    } catch (error) {
      alert(t('seo.resolve404Error', 'Failed to resolve 404'));
    }
  };

  const saveProgrammaticConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/seo/programmatic-pages`, programmaticConfig, { headers });
      alert(t('common.success', 'Settings saved!'));
    } catch (error) {
      alert(t('common.error', 'Failed to save settings'));
    } finally {
      setSaving(false);
    }
  };

  // ===== Rank Checker Functions =====
  const addRankKeyword = async () => {
    if (!newKeyword.trim()) return;
    try {
      const res = await axios.post(`${API}/seo/rank-keywords`, {
        keyword: newKeyword.trim(),
        domain: globalSettings.rank_checker_domain || ''
      }, { headers });
      setRankKeywords([res.data, ...rankKeywords]);
      setNewKeyword('');
    } catch (error) {
      alert(error.response?.data?.detail || t('seo.keywordAddError', 'Failed to add keyword'));
    }
  };

  const deleteRankKeyword = async (id) => {
    try {
      await axios.delete(`${API}/seo/rank-keywords/${id}`, { headers });
      setRankKeywords(rankKeywords.filter(k => k.id !== id));
    } catch (error) {
      alert(t('common.error', 'Delete error'));
    }
  };

  const checkKeywordRank = async (id) => {
    setRankChecking(id);
    try {
      const res = await axios.post(`${API}/seo/rank-check/${id}`, {}, { headers });
      setRankKeywords(rankKeywords.map(k =>
        k.id === id ? { ...k, last_position: res.data.position, last_url: res.data.url, last_checked: res.data.checked_at } : k
      ));
    } catch (error) {
      alert(error.response?.data?.detail || t('seo.rankCheckError', 'Rank check error'));
    } finally {
      setRankChecking(null);
    }
  };

  const checkAllRanks = async () => {
    setRankCheckingAll(true);
    try {
      await axios.post(`${API}/seo/rank-check-all`, {}, { headers });
      // Refresh results
      const res = await axios.get(`${API}/seo/rank-keywords`, { headers });
      setRankKeywords(res.data || []);
    } catch (error) {
      alert(error.response?.data?.detail || t('seo.bulkCheckError', 'Bulk check error'));
    } finally {
      setRankCheckingAll(false);
    }
  };

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-zinc-800 flex items-center justify-center">
        <p className="text-zinc-400">{t('admin.accessRequired', 'Admin access required')}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-800 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  const menuItems = [
    { id: 'dashboard', label: t('seo.seoDashboard', 'SEO Dashboard'), icon: BarChart3 },
    { id: 'global', label: t('seo.globalSettings', 'Global Settings'), icon: Settings2 },
    { id: 'templates', label: t('seo.seoTemplates', 'SEO Templates'), icon: FileText },
    { id: 'redirects', label: t('seo.redirects', 'Redirects'), icon: ArrowRight },
    { id: '404', label: t('seo.monitor404', '404 Monitor'), icon: XCircle },
    { id: 'schemas', label: t('seo.jsonLdSchemas', 'JSON-LD Schemas'), icon: Code },
    { id: 'serp', label: t('seo.previewCheck', 'Preview & Check'), icon: Eye },
    { id: 'sitemap', label: t('seo.sitemapRobots', 'Sitemap & Robots'), icon: Map },
    { id: 'programmatic', label: t('seo.programmaticSeo', 'Programmatic SEO'), icon: Layers },
    { id: 'hreflang', label: t('seo.multiLanguage', 'Multi-Language'), icon: Globe },
    { id: 'rank-checker', label: t('seo.rankTracker', 'Rank Tracker'), icon: Search },
  ];

  return (
    <div className="min-h-screen bg-zinc-800">
      {/* Breadcrumb */}
      <Breadcrumb items={[
        { label: t('admin.adminPanel', 'Admin Panel'), href: '/admin' },
        { label: t('seo.seoManagement', 'SEO Management'), href: null }
      ]} />

      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <div className="flex items-center gap-2">
            <Search className="w-6 h-6 text-emerald-500" />
            <h1 className="text-2xl font-bold text-white">{t('seo.seoManagement', 'SEO Management')}</h1>
          </div>
        </div>

        <div className="flex gap-6">
          {/* Sidebar */}
          <div className="w-64 flex-shrink-0">
            <nav className="bg-zinc-900 rounded-lg border border-zinc-800 p-4 sticky top-8 max-h-[calc(100vh-4rem)] overflow-y-auto">
              <div className="space-y-1">
                {menuItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                      activeTab === item.id 
                        ? 'bg-emerald-600 text-white' 
                        : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                    }`}
                  >
                    <item.icon className="w-4 h-4" />
                    <span className="text-sm">{item.label}</span>
                  </button>
                ))}
              </div>
            </nav>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Dashboard Tab */}
            {activeTab === 'dashboard' && (
              <div className="space-y-6">
                <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
                  <h2 className="text-xl font-semibold text-white mb-4">{t('seo.auditSummary', 'SEO Audit Summary')}</h2>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <StatCard icon={AlertTriangle} title={t('seo.issues', 'Issues')} value={audit.issues?.length || 0} color="red" />
                    <StatCard icon={AlertTriangle} title={t('seo.warnings', 'Warnings')} value={audit.warnings?.length || 0} color="yellow" />
                    <StatCard icon={CheckCircle} title={t('seo.passed', 'Passed')} value={audit.passed?.length || 0} color="emerald" />
                    <StatCard icon={Link2} title={t('seo.redirects', 'Redirects')} value={audit.stats?.active_redirects || 0} color="blue" />
                  </div>

                  {/* Issues */}
                  {audit.issues?.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-red-400 font-medium mb-3">{t('seo.criticalIssues', 'Critical Issues')}</h3>
                      <div className="space-y-2">
                        {audit.issues.map((issue, idx) => (
                          <div key={idx} className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 flex items-start gap-3">
                            <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-white">{issue.message}</p>
                              <p className="text-zinc-500 text-sm">{t('seo.type', 'Type')}: {issue.type}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Warnings */}
                  {audit.warnings?.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-yellow-400 font-medium mb-3">{t('seo.warnings', 'Warnings')}</h3>
                      <div className="space-y-2">
                        {audit.warnings.map((warning, idx) => (
                          <div key={idx} className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3 flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-white">{warning.message}</p>
                              <p className="text-zinc-500 text-sm">{t('seo.severity', 'Severity')}: {warning.severity}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Passed */}
                  {audit.passed?.length > 0 && (
                    <div>
                      <h3 className="text-emerald-400 font-medium mb-3">{t('seo.passedChecks', 'Passed Checks')}</h3>
                      <div className="space-y-2">
                        {audit.passed.map((item, idx) => (
                          <div key={idx} className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3 flex items-center gap-3">
                            <CheckCircle className="w-5 h-5 text-emerald-400" />
                            <p className="text-white">{item}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Global Settings Tab */}
            {activeTab === 'global' && (
              <div className="space-y-6">
                <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-white">{t('seo.globalSeoSettings', 'Global SEO Settings')}</h2>
                    <Button onClick={saveGlobalSettings} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
                      <Save className="w-4 h-4 mr-2" />
                      {saving ? t('common.saving', 'Saving...') : t('common.saveChanges', 'Save Changes')}
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-zinc-300">{t('seo.siteName', 'Site Name')}</Label>
                      <Input
                        value={globalSettings.site_name || ''}
                        onChange={(e) => setGlobalSettings({ ...globalSettings, site_name: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">{t('seo.siteTagline', 'Site Tagline')}</Label>
                      <Input
                        value={globalSettings.site_tagline || ''}
                        onChange={(e) => setGlobalSettings({ ...globalSettings, site_tagline: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                      />
                    </div>
                  </div>

                  <div className="mt-6 space-y-2">
                    <Label className="text-zinc-300">{t('seo.defaultTitleTemplate', 'Default Title Template')}</Label>
                    <Input
                      value={globalSettings.default_title_template || ''}
                      onChange={(e) => setGlobalSettings({ ...globalSettings, default_title_template: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-white"
                      placeholder="{page_title} | {site_name}"
                    />
                    <p className="text-zinc-500 text-sm">{t('seo.variables', 'Variables')}: {'{page_title}'}, {'{site_name}'}, {'{category}'}, {'{city}'}</p>
                  </div>

                  <div className="mt-6 space-y-2">
                    <Label className="text-zinc-300">{t('seo.defaultMetaDescription', 'Default Meta Description')}</Label>
                    <Textarea
                      value={globalSettings.default_meta_description || ''}
                      onChange={(e) => setGlobalSettings({ ...globalSettings, default_meta_description: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-white"
                      rows={3}
                    />
                  </div>

                  <div className="mt-6 space-y-2">
                    <Label className="text-zinc-300">{t('seo.defaultKeywords', 'Default Keywords')}</Label>
                    <Input
                      value={globalSettings.default_keywords || ''}
                      onChange={(e) => setGlobalSettings({ ...globalSettings, default_keywords: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-white"
                      placeholder="keyword1, keyword2, keyword3"
                    />
                  </div>

                  <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-zinc-300">{t('seo.defaultOgImageUrl', 'Default OG Image URL')}</Label>
                      <Input
                        value={globalSettings.default_og_image || ''}
                        onChange={(e) => setGlobalSettings({ ...globalSettings, default_og_image: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">{t('seo.twitterHandle', 'Twitter Handle')}</Label>
                      <Input
                        value={globalSettings.twitter_handle || ''}
                        onChange={(e) => setGlobalSettings({ ...globalSettings, twitter_handle: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        placeholder="@yourhandle"
                      />
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-zinc-800">
                    <h3 className="text-white font-medium mb-4">{t('seo.analyticsVerification', 'Analytics & Verification')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label className="text-zinc-300">{t('seo.googleAnalyticsId', 'Google Analytics ID')}</Label>
                        <Input
                          value={globalSettings.google_analytics_id || ''}
                          onChange={(e) => setGlobalSettings({ ...globalSettings, google_analytics_id: e.target.value })}
                          className="bg-zinc-800 border-zinc-700 text-white"
                          placeholder="G-XXXXXXXXXX"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">{t('seo.googleTagManagerId', 'Google Tag Manager ID')}</Label>
                        <Input
                          value={globalSettings.google_tag_manager_id || ''}
                          onChange={(e) => setGlobalSettings({ ...globalSettings, google_tag_manager_id: e.target.value })}
                          className="bg-zinc-800 border-zinc-700 text-white"
                          placeholder="GTM-XXXXXXX"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">{t('seo.googleSearchConsole', 'Google Search Console')}</Label>
                        <Input
                          value={globalSettings.google_search_console || ''}
                          onChange={(e) => setGlobalSettings({ ...globalSettings, google_search_console: e.target.value })}
                          className="bg-zinc-800 border-zinc-700 text-white"
                          placeholder={t('seo.verificationCode', 'Verification code')}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">{t('seo.bingVerification', 'Bing Verification')}</Label>
                        <Input
                          value={globalSettings.bing_verification || ''}
                          onChange={(e) => setGlobalSettings({ ...globalSettings, bing_verification: e.target.value })}
                          className="bg-zinc-800 border-zinc-700 text-white"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-zinc-800">
                    <h3 className="text-white font-medium mb-4">{t('seo.rankTrackerSettings', 'Rank Tracker Settings')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Custom Search API Key</Label>
                        <Input
                          value={globalSettings.google_custom_search_api_key || ''}
                          onChange={(e) => setGlobalSettings({ ...globalSettings, google_custom_search_api_key: e.target.value })}
                          className="bg-zinc-800 border-zinc-700 text-white"
                          placeholder="AIzaSy..."
                          type="password"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Custom Search Engine ID (CX)</Label>
                        <Input
                          value={globalSettings.google_custom_search_cx || ''}
                          onChange={(e) => setGlobalSettings({ ...globalSettings, google_custom_search_cx: e.target.value })}
                          className="bg-zinc-800 border-zinc-700 text-white"
                          placeholder="a1b2c3d4e5f6..."
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label className="text-zinc-300">{t('seo.siteDomainForRankTracker', 'Site Domain (for Rank Tracker)')}</Label>
                        <Input
                          value={globalSettings.rank_checker_domain || ''}
                          onChange={(e) => setGlobalSettings({ ...globalSettings, rank_checker_domain: e.target.value })}
                          className="bg-zinc-800 border-zinc-700 text-white"
                          placeholder="example.com"
                        />
                        <p className="text-zinc-500 text-sm">{t('seo.domainWithoutProtocol', 'Domain to use for rank checking (without protocol)')}</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-zinc-800">
                    <h3 className="text-white font-medium mb-4">{t('seo.defaultRobots', 'Default Robots')}</h3>
                    <Select 
                      value={globalSettings.default_robots || 'index, follow'}
                      onValueChange={(v) => setGlobalSettings({ ...globalSettings, default_robots: v })}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white w-64">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        <SelectItem value="index, follow" className="text-zinc-300">Index, Follow</SelectItem>
                        <SelectItem value="index, nofollow" className="text-zinc-300">Index, NoFollow</SelectItem>
                        <SelectItem value="noindex, follow" className="text-zinc-300">NoIndex, Follow</SelectItem>
                        <SelectItem value="noindex, nofollow" className="text-zinc-300">NoIndex, NoFollow</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="mt-8 pt-6 border-t border-zinc-800">
                    <h3 className="text-white font-medium mb-4">{t('seo.featureSettings', 'Feature Settings')}</h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white">{t('seo.enableJsonLd', 'Enable JSON-LD Schemas')}</p>
                          <p className="text-zinc-500 text-sm">{t('seo.addStructuredData', 'Add structured data to pages')}</p>
                        </div>
                        <Switch
                          checked={globalSettings.enable_json_ld !== false}
                          onCheckedChange={(checked) => setGlobalSettings({ ...globalSettings, enable_json_ld: checked })}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white">{t('seo.enableOpenGraph', 'Enable Open Graph')}</p>
                          <p className="text-zinc-500 text-sm">{t('seo.facebookLinkedinSharing', 'Facebook/LinkedIn sharing')}</p>
                        </div>
                        <Switch
                          checked={globalSettings.enable_open_graph !== false}
                          onCheckedChange={(checked) => setGlobalSettings({ ...globalSettings, enable_open_graph: checked })}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white">{t('seo.enableTwitterCards', 'Enable Twitter Cards')}</p>
                          <p className="text-zinc-500 text-sm">{t('seo.twitterSharePreview', 'Twitter share preview')}</p>
                        </div>
                        <Switch
                          checked={globalSettings.enable_twitter_cards !== false}
                          onCheckedChange={(checked) => setGlobalSettings({ ...globalSettings, enable_twitter_cards: checked })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Redirects Tab */}
            {activeTab === 'redirects' && (
              <div className="space-y-6">
                <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-white">{t('seo.redirects301302', '301/302 Redirects')}</h2>
                    <Button onClick={() => setNewRedirectModal(true)} className="bg-emerald-600 hover:bg-emerald-700">
                      <Plus className="w-4 h-4 mr-2" />
                      {t('seo.addRedirect', 'Add Redirect')}
                    </Button>
                  </div>

                  {redirects.length === 0 ? (
                    <p className="text-zinc-400 text-center py-8">{t('seo.noRedirects', 'No redirects configured')}</p>
                  ) : (
                    <div className="space-y-3">
                      {redirects.map((redirect) => (
                        <div key={redirect.id} className="bg-zinc-800 rounded-lg p-4 flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className={redirect.redirect_type === 301 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400'}>
                                {redirect.redirect_type}
                              </Badge>
                              {!redirect.is_active && <Badge className="bg-red-500/20 text-red-400">{t('seo.inactive', 'Inactive')}</Badge>}
                            </div>
                            <p className="text-white font-mono text-sm truncate">{redirect.source_url}</p>
                            <div className="flex items-center gap-2 text-zinc-400 text-sm">
                              <ArrowRight className="w-4 h-4" />
                              <span className="truncate">{redirect.target_url}</span>
                            </div>
                            <p className="text-zinc-500 text-xs mt-1">{t('seo.hits', 'Hits')}: {redirect.hit_count || 0}</p>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingRedirect(redirect)}
                              className="text-zinc-400 hover:text-white"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="bg-zinc-900 border-zinc-800">
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="text-white">{t('seo.deleteRedirect', 'Delete Redirect')}</AlertDialogTitle>
                                  <AlertDialogDescription className="text-zinc-400">
                                    {t('seo.confirmDeleteRedirect', 'Are you sure you want to delete this redirect?')}
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel className="bg-zinc-800 text-zinc-300 border-zinc-700">{t('common.cancel', 'Cancel')}</AlertDialogCancel>
                                  <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => deleteRedirect(redirect.id)}>
                                    {t('common.delete', 'Delete')}
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* New Redirect Modal */}
                {newRedirectModal && (
                  <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                    <div className="bg-zinc-900 rounded-xl border border-zinc-800 w-full max-w-lg p-6">
                      <h3 className="text-xl font-bold text-white mb-4">{t('seo.addRedirect', 'Add Redirect')}</h3>
                      <NewRedirectForm onSubmit={createRedirect} onCancel={() => setNewRedirectModal(false)} />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 404 Monitor Tab */}
            {activeTab === '404' && (
              <div className="space-y-6">
                <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-white">{t('seo.errorMonitor404', '404 Error Monitor')}</h2>
                    <Button onClick={fetchAllData} variant="outline" className="border-zinc-700 text-zinc-300">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      {t('common.refresh', 'Refresh')}
                    </Button>
                  </div>

                  {notFoundLogs.length === 0 ? (
                    <p className="text-zinc-400 text-center py-8">{t('seo.no404Errors', 'No recorded 404 errors')}</p>
                  ) : (
                    <div className="space-y-3">
                      {notFoundLogs.filter(log => !log.is_resolved).map((log, idx) => (
                        <div key={idx} className="bg-zinc-800 rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <p className="text-white font-mono text-sm truncate">{log.url}</p>
                              <div className="flex items-center gap-4 text-zinc-500 text-xs mt-1">
                                <span>{t('seo.hits', 'Hits')}: {log.hit_count}</span>
                                <span>{t('seo.first', 'First')}: {new Date(log.first_seen).toLocaleDateString('en-US')}</span>
                                <span>{t('seo.last', 'Last')}: {new Date(log.last_seen).toLocaleDateString('en-US')}</span>
                              </div>
                              {log.referrer && (
                                <p className="text-zinc-500 text-xs mt-1">{t('seo.referrer', 'Referrer')}: {log.referrer}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 ml-4">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  const target = prompt(t('seo.enterRedirectTarget', 'Enter redirect target URL (leave empty to just dismiss):'));
                                  if (target !== null) {
                                    resolve404(log.url, target);
                                  }
                                }}
                                className="border-emerald-700 text-emerald-400"
                              >
                                {t('seo.resolve', 'Resolve')}
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Sitemap & Robots Tab */}
            {activeTab === 'sitemap' && (
              <div className="space-y-6">
                {/* Sitemap Configuration */}
                <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-white">{t('seo.sitemapConfiguration', 'Sitemap Configuration')}</h2>
                    <Button onClick={saveSitemapConfig} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
                      <Save className="w-4 h-4 mr-2" />
                      {saving ? t('common.saving', 'Saving...') : t('common.saveSettings', 'Save Settings')}
                    </Button>
                  </div>
                  <p className="text-zinc-400 text-sm mb-6">
                    {t('seo.sitemapDesc', 'Configure which content types to include in your sitemap. Changes are applied immediately when saved.')}
                  </p>
                  
                  {/* Content Types */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-blue-400" />
                        <div>
                          <p className="text-white font-medium">{t('seo.pages', 'Pages')}</p>
                          <p className="text-zinc-500 text-sm">{t('seo.staticDynamicPages', 'Static & dynamic pages')}</p>
                        </div>
                      </div>
                      <Switch
                        checked={sitemapConfig.include_pages}
                        onCheckedChange={(checked) => setSitemapConfig({ ...sitemapConfig, include_pages: checked })}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Layers className="w-5 h-5 text-purple-400" />
                        <div>
                          <p className="text-white font-medium">{t('seo.categories', 'Categories')}</p>
                          <p className="text-zinc-500 text-sm">{t('seo.categoryCityPages', 'Category & city pages')}</p>
                        </div>
                      </div>
                      <Switch
                        checked={sitemapConfig.include_categories}
                        onCheckedChange={(checked) => setSitemapConfig({ ...sitemapConfig, include_categories: checked })}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Eye className="w-5 h-5 text-emerald-400" />
                        <div>
                          <p className="text-white font-medium">{t('seo.listings', 'Listings')}</p>
                          <p className="text-zinc-500 text-sm">{t('seo.allActiveListings', 'All active listings')}</p>
                        </div>
                      </div>
                      <Switch
                        checked={sitemapConfig.include_listings}
                        onCheckedChange={(checked) => setSitemapConfig({ ...sitemapConfig, include_listings: checked })}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-yellow-400" />
                        <div>
                          <p className="text-white font-medium">Blog</p>
                          <p className="text-zinc-500 text-sm">{t('seo.publishedBlogPosts', 'Published blog posts')}</p>
                        </div>
                      </div>
                      <Switch
                        checked={sitemapConfig.include_blog}
                        onCheckedChange={(checked) => setSitemapConfig({ ...sitemapConfig, include_blog: checked })}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg md:col-span-2">
                      <div className="flex items-center gap-3">
                        <Map className="w-5 h-5 text-pink-400" />
                        <div>
                          <p className="text-white font-medium">{t('seo.imageSitemap', 'Image Sitemap')}</p>
                          <p className="text-zinc-500 text-sm">{t('seo.imageSitemapDesc', 'Separate sitemap for images (with alt text)')}</p>
                        </div>
                      </div>
                      <Switch
                        checked={sitemapConfig.include_images}
                        onCheckedChange={(checked) => setSitemapConfig({ ...sitemapConfig, include_images: checked })}
                      />
                    </div>
                  </div>

                  {/* Priority Settings */}
                  <h3 className="text-white font-medium mb-4">{t('seo.prioritySettings', 'Priority Settings')}</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label className="text-zinc-400 text-sm">{t('seo.listings', 'Listings')}</Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        max="1"
                        value={sitemapConfig.listings_priority}
                        onChange={(e) => setSitemapConfig({ ...sitemapConfig, listings_priority: parseFloat(e.target.value) })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-400 text-sm">Blog</Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        max="1"
                        value={sitemapConfig.blog_priority}
                        onChange={(e) => setSitemapConfig({ ...sitemapConfig, blog_priority: parseFloat(e.target.value) })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-400 text-sm">{t('seo.categories', 'Categories')}</Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        max="1"
                        value={sitemapConfig.categories_priority}
                        onChange={(e) => setSitemapConfig({ ...sitemapConfig, categories_priority: parseFloat(e.target.value) })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-400 text-sm">{t('seo.pages', 'Pages')}</Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        max="1"
                        value={sitemapConfig.pages_priority}
                        onChange={(e) => setSitemapConfig({ ...sitemapConfig, pages_priority: parseFloat(e.target.value) })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                      />
                    </div>
                  </div>
                </div>

                {/* Sitemap Links */}
                <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
                  <h2 className="text-xl font-semibold text-white mb-4">{t('seo.sitemapFiles', 'Sitemap Files')}</h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <a 
                      href={`${BACKEND_URL}/api/seo/sitemap-index.xml`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg hover:bg-zinc-700 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Map className="w-5 h-5 text-emerald-400" />
                        <span className="text-white">{t('seo.sitemapIndex', 'Sitemap Index')}</span>
                      </div>
                      <ExternalLink className="w-4 h-4 text-zinc-400" />
                    </a>
                    <a 
                      href={`${BACKEND_URL}/api/seo/sitemap.xml`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg hover:bg-zinc-700 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-blue-400" />
                        <span className="text-white">{t('seo.mainSitemap', 'Main Sitemap')}</span>
                      </div>
                      <ExternalLink className="w-4 h-4 text-zinc-400" />
                    </a>
                    <a 
                      href={`${BACKEND_URL}/api/seo/sitemap-images.xml`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg hover:bg-zinc-700 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Map className="w-5 h-5 text-pink-400" />
                        <span className="text-white">{t('seo.imageSitemap', 'Image Sitemap')}</span>
                      </div>
                      <ExternalLink className="w-4 h-4 text-zinc-400" />
                    </a>
                  </div>
                </div>

                <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-white">Robots.txt</h2>
                    <Button onClick={saveGlobalSettings} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
                      <Save className="w-4 h-4 mr-2" />
                      {t('common.save', 'Save')}
                    </Button>
                  </div>
                  <Textarea
                    value={globalSettings.robots_txt || ''}
                    onChange={(e) => setGlobalSettings({ ...globalSettings, robots_txt: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm"
                    rows={12}
                  />
                  <div className="flex items-center gap-4 mt-4">
                    <a
                      href={`${BACKEND_URL}/api/seo/robots.txt`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-emerald-400 hover:text-emerald-300 flex items-center gap-2"
                    >
                      <ExternalLink className="w-4 h-4" />
                      {t('seo.viewRobotsTxt', 'View Robots.txt')}
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Templates Tab */}
            {activeTab === 'templates' && (
              <div className="space-y-6">
                <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-white">{t('seo.seoTemplates', 'SEO Templates')}</h2>
                    <Button onClick={() => setEditingTemplate({ name: '', page_type: 'listing', title_template: '', description_template: '', is_default: false })} className="bg-emerald-600 hover:bg-emerald-700">
                      <Plus className="w-4 h-4 mr-2" />
                      {t('seo.addTemplate', 'Add Template')}
                    </Button>
                  </div>
                  <p className="text-zinc-400 text-sm mb-6">
                    {t('seo.templatesDesc', 'Create templates to auto-generate SEO titles and descriptions. Use variables like')} {'{name}'}, {'{category}'}, {'{city}'}, {'{site_name}'}.
                  </p>

                  {templates.length === 0 ? (
                    <p className="text-zinc-500 text-center py-8">{t('seo.noTemplates', 'No templates yet')}</p>
                  ) : (
                    <div className="space-y-3">
                      {templates.map((template) => (
                        <div key={template.id} className="bg-zinc-800 rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="text-white font-medium">{template.name}</h3>
                                <Badge className="bg-blue-500/20 text-blue-400">{template.page_type}</Badge>
                                {template.is_default && <Badge className="bg-emerald-500/20 text-emerald-400">{t('seo.default', 'Default')}</Badge>}
                              </div>
                              <p className="text-zinc-400 text-sm font-mono">{template.title_template}</p>
                              <p className="text-zinc-500 text-xs mt-1">{template.description_template}</p>
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => setEditingTemplate(template)} className="text-zinc-400 hover:text-white">
                              <Edit className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Template Edit Modal */}
                {editingTemplate && (
                  <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                    <div className="bg-zinc-900 rounded-xl border border-zinc-800 w-full max-w-lg p-6">
                      <h3 className="text-xl font-bold text-white mb-4">
                        {editingTemplate.id ? t('seo.editTemplate', 'Edit Template') : t('seo.newTemplate', 'New Template')}
                      </h3>
                      <TemplateForm 
                        template={editingTemplate} 
                        onSave={async (data) => {
                          if (editingTemplate.id) {
                            await axios.put(`${API}/seo/templates/${editingTemplate.id}`, data, { headers });
                          } else {
                            await axios.post(`${API}/seo/templates`, data, { headers });
                          }
                          fetchAllData();
                          setEditingTemplate(null);
                        }}
                        onCancel={() => setEditingTemplate(null)}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* JSON-LD Schemas Tab */}
            {activeTab === 'schemas' && (
              <div className="space-y-6">
                {/* Ready Schema Templates */}
                <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
                  <h2 className="text-xl font-semibold text-white mb-2">{t('seo.readySchemaTemplates', 'Ready Schema Templates')}</h2>
                  <p className="text-zinc-500 text-sm mb-5">{t('seo.schemaTemplatesDesc', 'Select a schema type to quickly add. Content will be auto-filled.')}</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { type: 'Organization', label: t('seo.organization', 'Organization'), desc: t('seo.orgDesc', 'Company/organization info'), icon: '🏢', template: JSON.stringify({"@context":"https://schema.org","@type":"Organization","name":"","url":"","logo":"","description":"","sameAs":[],"contactPoint":{"@type":"ContactPoint","telephone":"","contactType":"customer service","availableLanguage":"English"}}, null, 2) },
                      { type: 'LocalBusiness', label: t('seo.localBusiness', 'Local Business'), desc: t('seo.localBusinessDesc', 'Physical location info'), icon: '📍', template: JSON.stringify({"@context":"https://schema.org","@type":"LocalBusiness","name":"","image":"","url":"","telephone":"","address":{"@type":"PostalAddress","streetAddress":"","addressLocality":"","addressRegion":"","postalCode":"","addressCountry":"US"},"openingHoursSpecification":[{"@type":"OpeningHoursSpecification","dayOfWeek":["Monday","Tuesday","Wednesday","Thursday","Friday"],"opens":"09:00","closes":"18:00"}],"priceRange":"$$"}, null, 2) },
                      { type: 'WebSite', label: t('seo.website', 'Website'), desc: t('seo.websiteDesc', 'Site search box schema'), icon: '🌐', template: JSON.stringify({"@context":"https://schema.org","@type":"WebSite","name":"","url":"","potentialAction":{"@type":"SearchAction","target":{"@type":"EntryPoint","urlTemplate":"{search_url}?q={search_term_string}"},"query-input":"required name=search_term_string"}}, null, 2) },
                      { type: 'FAQPage', label: t('seo.faqPage', 'FAQ Page'), desc: t('seo.faqDesc', 'Frequently asked questions'), icon: '❓', template: JSON.stringify({"@context":"https://schema.org","@type":"FAQPage","mainEntity":[{"@type":"Question","name":"Question 1?","acceptedAnswer":{"@type":"Answer","text":"Answer 1."}},{"@type":"Question","name":"Question 2?","acceptedAnswer":{"@type":"Answer","text":"Answer 2."}}]}, null, 2) },
                      { type: 'Service', label: t('seo.service', 'Service'), desc: t('seo.serviceDesc', 'Service offering details'), icon: '⚙️', template: JSON.stringify({"@context":"https://schema.org","@type":"Service","name":"","description":"","provider":{"@type":"Organization","name":""},"areaServed":{"@type":"Country","name":"United States"},"serviceType":"","offers":{"@type":"Offer","price":"","priceCurrency":"USD"}}, null, 2) },
                      { type: 'Person', label: t('seo.person', 'Person'), desc: t('seo.personDesc', 'Freelancer/person profile'), icon: '👤', template: JSON.stringify({"@context":"https://schema.org","@type":"Person","name":"","jobTitle":"","url":"","image":"","sameAs":[],"worksFor":{"@type":"Organization","name":""},"knowsAbout":[]}, null, 2) },
                      { type: 'HowTo', label: t('seo.howTo', 'How To'), desc: t('seo.howToDesc', 'Step by step guide'), icon: '📝', template: JSON.stringify({"@context":"https://schema.org","@type":"HowTo","name":"","description":"","step":[{"@type":"HowToStep","name":"Step 1","text":"Description 1"},{"@type":"HowToStep","name":"Step 2","text":"Description 2"}]}, null, 2) },
                      { type: 'Product', label: t('seo.product', 'Product'), desc: t('seo.productDesc', 'Product info and pricing'), icon: '📦', template: JSON.stringify({"@context":"https://schema.org","@type":"Product","name":"","description":"","image":"","brand":{"@type":"Brand","name":""},"offers":{"@type":"Offer","price":"","priceCurrency":"USD","availability":"https://schema.org/InStock"},"aggregateRating":{"@type":"AggregateRating","ratingValue":"4.5","reviewCount":"10"}}, null, 2) },
                    ].map((preset) => (
                      <button
                        key={preset.type}
                        onClick={() => setEditingSchema({ name: preset.label, schema_type: preset.type, template: preset.template, is_active: true })}
                        className="bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 hover:border-emerald-500/30 rounded-xl p-4 text-left transition-all group"
                      >
                        <span className="text-2xl mb-2 block">{preset.icon}</span>
                        <h4 className="text-white font-medium text-sm group-hover:text-emerald-400 transition-colors">{preset.label}</h4>
                        <p className="text-zinc-500 text-xs mt-1">{preset.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Existing Schemas */}
                <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-white">{t('seo.savedSchemas', 'Saved Schemas')}</h2>
                    <Button onClick={() => setEditingSchema({ name: '', schema_type: 'Organization', template: '{\n  "@context": "https://schema.org",\n  "@type": "Organization"\n}', is_active: true })} className="bg-emerald-600 hover:bg-emerald-700">
                      <Plus className="w-4 h-4 mr-2" />
                      {t('seo.addEmptySchema', 'Add Empty Schema')}
                    </Button>
                  </div>

                  {schemas.length === 0 ? (
                    <div className="text-center py-12">
                      <Code className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                      <p className="text-zinc-500">{t('seo.noSchemasYet', 'No schemas added yet')}</p>
                      <p className="text-zinc-600 text-sm mt-1">{t('seo.startWithTemplate', 'Get started by selecting one of the ready templates above')}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {schemas.map((schema) => {
                        let parsedTemplate = {};
                        try { parsedTemplate = JSON.parse(schema.template || '{}'); } catch {}
                        return (
                          <div key={schema.id} className="bg-zinc-800 rounded-lg p-4 hover:border-zinc-600 border border-transparent transition-colors">
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-2">
                                  <h3 className="text-white font-medium">{schema.name}</h3>
                                  <Badge className="bg-purple-500/20 text-purple-400 text-xs">{schema.schema_type}</Badge>
                                  {schema.is_active ? (
                                    <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">{t('seo.active', 'Active')}</Badge>
                                  ) : (
                                    <Badge className="bg-zinc-600/20 text-zinc-500 text-xs">{t('seo.inactive', 'Inactive')}</Badge>
                                  )}
                                </div>
                                <div className="bg-zinc-900 rounded-lg p-3 mt-2">
                                  <pre className="text-zinc-400 text-xs font-mono overflow-x-auto max-h-24 whitespace-pre-wrap">{JSON.stringify(parsedTemplate, null, 2).substring(0, 300)}{schema.template?.length > 300 ? '...' : ''}</pre>
                                </div>
                              </div>
                              <div className="flex gap-1 ml-3">
                                <Button variant="ghost" size="sm" onClick={() => setEditingSchema(schema)} className="text-zinc-400 hover:text-white">
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={async () => {
                                  if (window.confirm(t('seo.confirmDeleteSchema', 'Are you sure you want to delete this schema?'))) {
                                    await axios.delete(`${API}/seo/schemas/${schema.id}`, { headers });
                                    fetchAllData();
                                  }
                                }} className="text-zinc-400 hover:text-red-400">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Schema Validation Tips */}
                <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
                  <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                    {t('seo.schemaValidation', 'Schema Validation')}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <a href="https://search.google.com/test/rich-results" target="_blank" rel="noopener noreferrer" className="bg-zinc-800 hover:bg-zinc-700 rounded-lg p-4 flex items-center gap-3 transition-colors group">
                      <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                        <Search className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium group-hover:text-emerald-400 transition-colors">Google Rich Results Test</p>
                        <p className="text-zinc-500 text-xs">{t('seo.checkRichResults', 'Check rich results eligibility')}</p>
                      </div>
                      <ExternalLink className="w-4 h-4 text-zinc-600 ml-auto" />
                    </a>
                    <a href="https://validator.schema.org/" target="_blank" rel="noopener noreferrer" className="bg-zinc-800 hover:bg-zinc-700 rounded-lg p-4 flex items-center gap-3 transition-colors group">
                      <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                        <Code className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium group-hover:text-emerald-400 transition-colors">Schema.org Validator</p>
                        <p className="text-zinc-500 text-xs">{t('seo.validateJsonLd', 'Validate JSON-LD structure')}</p>
                      </div>
                      <ExternalLink className="w-4 h-4 text-zinc-600 ml-auto" />
                    </a>
                  </div>
                </div>

                {/* Schema Edit Modal */}
                {editingSchema && (
                  <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                    <div className="bg-zinc-900 rounded-xl border border-zinc-800 w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6">
                      <h3 className="text-xl font-bold text-white mb-4">
                        {editingSchema.id ? t('seo.editSchema', 'Edit Schema') : t('seo.newSchema', 'New Schema')}
                      </h3>
                      <SchemaForm
                        schema={editingSchema}
                        onSave={async (data) => {
                          try {
                            JSON.parse(data.template);
                          } catch {
                            alert(t('seo.invalidJson', 'Invalid JSON format! Please check the JSON-LD template.'));
                            return;
                          }
                          if (editingSchema.id) {
                            await axios.put(`${API}/seo/schemas/${editingSchema.id}`, data, { headers });
                          } else {
                            await axios.post(`${API}/seo/schemas`, data, { headers });
                          }
                          fetchAllData();
                          setEditingSchema(null);
                        }}
                        onCancel={() => setEditingSchema(null)}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* SERP Preview Tab */}
            {activeTab === 'serp' && (
              <div className="space-y-6">
                <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
                  <h2 className="text-xl font-semibold text-white mb-2">{t('seo.googleSerpPreview', 'Google SERP Preview')}</h2>
                  <p className="text-zinc-500 text-sm mb-6">{t('seo.serpDesc', 'Test how your page will appear in Google search results.')}</p>

                  <div className="space-y-4 mb-6">
                    <div className="space-y-2">
                      <Label className="text-zinc-300">{t('seo.pageTitle', 'Page Title')}</Label>
                      <Input
                        value={globalSettings.serp_preview_title || globalSettings.site_name || ''}
                        onChange={(e) => setGlobalSettings({ ...globalSettings, serp_preview_title: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        placeholder={t('seo.pageTitlePlaceholder', 'Page Title | Site Name')}
                      />
                      <p className="text-zinc-600 text-xs">{(globalSettings.serp_preview_title || globalSettings.site_name || '').length}/60 {t('seo.characters', 'characters')}</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">URL</Label>
                      <Input
                        value={globalSettings.serp_preview_url || (typeof window !== 'undefined' ? window.location.origin : '')}
                        onChange={(e) => setGlobalSettings({ ...globalSettings, serp_preview_url: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        placeholder="https://example.com/page"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">{t('seo.metaDescription', 'Meta Description')}</Label>
                      <Textarea
                        value={globalSettings.serp_preview_desc || globalSettings.default_meta_description || ''}
                        onChange={(e) => setGlobalSettings({ ...globalSettings, serp_preview_desc: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        rows={3}
                        placeholder={t('seo.pageDescPlaceholder', 'Page description...')}
                      />
                      <p className="text-zinc-600 text-xs">{(globalSettings.serp_preview_desc || globalSettings.default_meta_description || '').length}/160 {t('seo.characters', 'characters')}</p>
                    </div>
                  </div>

                  {/* Desktop Preview */}
                  <div className="mb-6">
                    <h3 className="text-zinc-400 text-sm font-medium mb-3">{t('seo.desktopPreview', 'Desktop Preview')}</h3>
                    <div className="bg-white rounded-xl p-6 max-w-2xl">
                      <div className="text-xs text-[#202124] flex items-center gap-1 mb-1">
                        <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4 rounded-full" onError={(e) => e.target.style.display='none'} />
                        <span>{(globalSettings.serp_preview_url || window.location.origin).replace('https://', '').replace('http://', '')}</span>
                      </div>
                      <h3 className="text-[#1a0dab] text-xl font-normal mb-1 hover:underline cursor-pointer leading-tight">
                        {(globalSettings.serp_preview_title || globalSettings.site_name || 'Page Title').substring(0, 60)}
                      </h3>
                      <p className="text-[#4d5156] text-sm leading-relaxed">
                        {(globalSettings.serp_preview_desc || globalSettings.default_meta_description || 'Meta description will appear here...').substring(0, 160)}
                      </p>
                    </div>
                  </div>

                  {/* Mobile Preview */}
                  <div>
                    <h3 className="text-zinc-400 text-sm font-medium mb-3">{t('seo.mobilePreview', 'Mobile Preview')}</h3>
                    <div className="bg-white rounded-xl p-4 max-w-sm">
                      <div className="text-xs text-[#202124] flex items-center gap-2 mb-1">
                        <div className="w-7 h-7 bg-gray-100 rounded-full flex items-center justify-center text-xs font-bold text-gray-600">
                          {(globalSettings.site_name || 'S').charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-[13px]">{globalSettings.site_name || 'Site Name'}</p>
                          <p className="text-[11px] text-gray-500">{(globalSettings.serp_preview_url || window.location.origin).replace('https://', '').replace('http://', '').substring(0, 40)}</p>
                        </div>
                      </div>
                      <h3 className="text-[#1a0dab] text-lg font-normal mt-2 hover:underline cursor-pointer leading-tight">
                        {(globalSettings.serp_preview_title || globalSettings.site_name || 'Page Title').substring(0, 60)}
                      </h3>
                      <p className="text-[#4d5156] text-[13px] leading-relaxed mt-1">
                        {(globalSettings.serp_preview_desc || globalSettings.default_meta_description || 'Meta description will appear here...').substring(0, 120)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* OG / Social Media Preview */}
                <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
                  <h2 className="text-xl font-semibold text-white mb-2">{t('seo.socialMediaPreview', 'Social Media Preview')}</h2>
                  <p className="text-zinc-500 text-sm mb-6">{t('seo.socialMediaDesc', 'Check how it will appear on Facebook, Twitter and LinkedIn.')}</p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Facebook/OG Preview */}
                    <div>
                      <h4 className="text-zinc-400 text-sm font-medium mb-3">Facebook / Open Graph</h4>
                      <div className="bg-[#f0f2f5] rounded-xl overflow-hidden max-w-sm">
                        <div className="aspect-[1.91/1] bg-zinc-300 flex items-center justify-center">
                          {globalSettings.default_og_image ? (
                            <img src={globalSettings.default_og_image} alt="OG" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-zinc-500 text-sm">OG Image (1200x630)</span>
                          )}
                        </div>
                        <div className="p-3">
                          <p className="text-[#65676b] text-xs uppercase">{(globalSettings.serp_preview_url || window.location.origin).replace('https://', '').replace('http://', '').split('/')[0]}</p>
                          <p className="text-[#1c1e21] font-semibold text-base mt-1 leading-tight">{(globalSettings.serp_preview_title || globalSettings.site_name || 'Page Title').substring(0, 60)}</p>
                          <p className="text-[#65676b] text-sm mt-1">{(globalSettings.serp_preview_desc || globalSettings.default_meta_description || 'Description...').substring(0, 80)}</p>
                        </div>
                      </div>
                    </div>

                    {/* Twitter Preview */}
                    <div>
                      <h4 className="text-zinc-400 text-sm font-medium mb-3">Twitter / X</h4>
                      <div className="bg-black rounded-2xl overflow-hidden max-w-sm border border-zinc-700">
                        <div className="aspect-[2/1] bg-zinc-800 flex items-center justify-center">
                          {globalSettings.default_og_image ? (
                            <img src={globalSettings.default_og_image} alt="Twitter" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-zinc-600 text-sm">Twitter Card Image</span>
                          )}
                        </div>
                        <div className="p-3">
                          <p className="text-white font-medium text-sm">{(globalSettings.serp_preview_title || globalSettings.site_name || 'Page Title').substring(0, 60)}</p>
                          <p className="text-zinc-500 text-xs mt-1">{(globalSettings.serp_preview_desc || globalSettings.default_meta_description || 'Description...').substring(0, 100)}</p>
                          <p className="text-zinc-600 text-xs mt-1">{(globalSettings.serp_preview_url || window.location.origin).replace('https://', '').replace('http://', '').split('/')[0]}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* SEO Checklist */}
                <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
                  <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                    {t('seo.seoChecklist', 'SEO Checklist')}
                  </h2>
                  <div className="space-y-2">
                    {[
                      { check: !!globalSettings.site_name, text: t('seo.checkSiteName', 'Site name is defined') },
                      { check: !!globalSettings.default_meta_description, text: t('seo.checkMetaDesc', 'Default meta description is set') },
                      { check: !!globalSettings.default_og_image, text: t('seo.checkOgImage', 'Default OG image is added') },
                      { check: !!globalSettings.google_analytics_id, text: t('seo.checkAnalytics', 'Google Analytics is connected') },
                      { check: !!globalSettings.google_search_console, text: t('seo.checkSearchConsole', 'Google Search Console is verified') },
                      { check: globalSettings.enable_json_ld !== false, text: t('seo.checkJsonLd', 'JSON-LD structured data is active') },
                      { check: globalSettings.enable_open_graph !== false, text: t('seo.checkOpenGraph', 'Open Graph tags are active') },
                      { check: globalSettings.enable_twitter_cards !== false, text: t('seo.checkTwitterCards', 'Twitter Cards are active') },
                      { check: schemas.length > 0, text: t('seo.checkSchemaAdded', 'At least one JSON-LD schema is added') },
                      { check: redirects.length > 0 || notFoundLogs.length === 0, text: t('seo.check404Managed', '404 errors are managed') },
                    ].map((item, idx) => (
                      <div key={idx} className={`flex items-center gap-3 rounded-lg p-3 ${item.check ? 'bg-emerald-500/5 border border-emerald-500/10' : 'bg-red-500/5 border border-red-500/10'}`}>
                        {item.check ? (
                          <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                        )}
                        <span className={`text-sm ${item.check ? 'text-zinc-300' : 'text-zinc-400'}`}>{item.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Programmatic SEO Tab */}
            {activeTab === 'programmatic' && (
              <div className="space-y-6">
                <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-white">{t('seo.programmaticSeoPages', 'Programmatic SEO Pages')}</h2>
                    <Button onClick={saveProgrammaticConfig} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
                      <Save className="w-4 h-4 mr-2" />
                      {saving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
                    </Button>
                  </div>
                  <p className="text-zinc-400 text-sm mb-6">
                    {t('seo.programmaticDesc', 'Automatically generate SEO-optimized landing pages for category + city combinations.')}
                  </p>

                  <div className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg mb-6">
                    <div>
                      <p className="text-white font-medium">{t('seo.enableProgrammaticPages', 'Enable Programmatic Pages')}</p>
                      <p className="text-zinc-500 text-sm">{t('seo.createLandingPages', 'Create /category/city landing pages')}</p>
                    </div>
                    <Switch
                      checked={programmaticConfig.enabled || false}
                      onCheckedChange={(checked) => setProgrammaticConfig({ ...programmaticConfig, enabled: checked })}
                    />
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-zinc-300">{t('seo.titleTemplate', 'Title Template')}</Label>
                      <Input
                        value={programmaticConfig.title_template || ''}
                        onChange={(e) => setProgrammaticConfig({ ...programmaticConfig, title_template: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        placeholder="{category} in {city} | {site_name}"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">{t('seo.metaDescTemplate', 'Meta Description Template')}</Label>
                      <Textarea
                        value={programmaticConfig.description_template || ''}
                        onChange={(e) => setProgrammaticConfig({ ...programmaticConfig, description_template: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        rows={2}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">{t('seo.h1Template', 'H1 Template')}</Label>
                      <Input
                        value={programmaticConfig.h1_template || ''}
                        onChange={(e) => setProgrammaticConfig({ ...programmaticConfig, h1_template: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        placeholder="{category} in {city}"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">{t('seo.contentTemplate', 'Content Template')}</Label>
                      <Textarea
                        value={programmaticConfig.content_template || ''}
                        onChange={(e) => setProgrammaticConfig({ ...programmaticConfig, content_template: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        rows={4}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Multi-Language Tab */}
            {activeTab === 'hreflang' && (
              <div className="space-y-6">
                <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
                  <h2 className="text-xl font-semibold text-white mb-6">{t('seo.multiLanguageSeo', 'Multi-Language SEO (Hreflang)')}</h2>
                  
                  <div className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg mb-6">
                    <div>
                      <p className="text-white font-medium">{t('seo.enableMultiLanguage', 'Enable Multi-Language')}</p>
                      <p className="text-zinc-500 text-sm">{t('seo.addHreflangTags', 'Add hreflang tags for international SEO')}</p>
                    </div>
                    <Switch
                      checked={hreflangConfig.enabled || false}
                      onCheckedChange={(checked) => setHreflangConfig({ ...hreflangConfig, enabled: checked })}
                    />
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-zinc-300">{t('seo.defaultLanguage', 'Default Language')}</Label>
                      <Input
                        value={hreflangConfig.default_language || 'en'}
                        onChange={(e) => setHreflangConfig({ ...hreflangConfig, default_language: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-white w-32"
                        placeholder="en"
                      />
                    </div>

                    <div>
                      <Label className="text-zinc-300 mb-3 block">{t('seo.configuredLanguages', 'Configured Languages')}</Label>
                      <div className="space-y-2">
                        {(hreflangConfig.languages || []).map((lang, idx) => (
                          <div key={idx} className="flex items-center gap-4 bg-zinc-800 rounded-lg p-3">
                            <Input
                              value={lang.code}
                              onChange={(e) => {
                                const newLangs = [...hreflangConfig.languages];
                                newLangs[idx].code = e.target.value;
                                setHreflangConfig({ ...hreflangConfig, languages: newLangs });
                              }}
                              className="bg-zinc-700 border-zinc-600 text-white w-20"
                              placeholder="en"
                            />
                            <Input
                              value={lang.name}
                              onChange={(e) => {
                                const newLangs = [...hreflangConfig.languages];
                                newLangs[idx].name = e.target.value;
                                setHreflangConfig({ ...hreflangConfig, languages: newLangs });
                              }}
                              className="bg-zinc-700 border-zinc-600 text-white flex-1"
                              placeholder="English"
                            />
                            <Input
                              value={lang.url_prefix}
                              onChange={(e) => {
                                const newLangs = [...hreflangConfig.languages];
                                newLangs[idx].url_prefix = e.target.value;
                                setHreflangConfig({ ...hreflangConfig, languages: newLangs });
                              }}
                              className="bg-zinc-700 border-zinc-600 text-white w-32"
                              placeholder="/en"
                            />
                            {lang.is_default && <Badge className="bg-emerald-500/20 text-emerald-400">{t('seo.default', 'Default')}</Badge>}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Rank Checker Tab */}
            {activeTab === 'rank-checker' && (
              <div className="space-y-6">
                <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-xl font-semibold text-white">{t('seo.googleRankTracker', 'Google Rank Tracker')}</h2>
                      <p className="text-zinc-500 text-sm mt-1">{t('seo.rankTrackerDesc', 'Track where your keywords rank on Google')}</p>
                    </div>
                    <Button
                      onClick={checkAllRanks}
                      disabled={rankCheckingAll || rankKeywords.length === 0}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      {rankCheckingAll ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                      {rankCheckingAll ? t('seo.checking', 'Checking...') : t('seo.checkAll', 'Check All')}
                    </Button>
                  </div>

                  {/* API Key warning */}
                  {(!globalSettings.google_custom_search_api_key || !globalSettings.google_custom_search_cx) && (
                    <div className="flex items-center gap-3 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg mb-6">
                      <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                      <div>
                        <p className="text-yellow-400 font-medium">{t('seo.apiConfigRequired', 'API Configuration Required')}</p>
                        <p className="text-yellow-400/70 text-sm">{t('seo.apiConfigDesc', 'Configure the Google Custom Search API Key and CX ID from the "Global Settings" tab for rank tracking.')}</p>
                      </div>
                    </div>
                  )}

                  {/* Add new keyword */}
                  <div className="flex gap-3 mb-6">
                    <Input
                      value={newKeyword}
                      onChange={(e) => setNewKeyword(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && addRankKeyword()}
                      className="bg-zinc-800 border-zinc-700 text-white flex-1"
                      placeholder={t('seo.enterKeyword', 'Enter keyword...')}
                    />
                    <Button onClick={addRankKeyword} className="bg-emerald-600 hover:bg-emerald-700">
                      <Plus className="w-4 h-4 mr-2" />
                      {t('common.add', 'Add')}
                    </Button>
                  </div>

                  {/* Results table */}
                  {rankKeywords.length === 0 ? (
                    <div className="text-center py-12">
                      <Search className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                      <p className="text-zinc-400">{t('seo.noKeywords', 'No tracked keywords yet')}</p>
                      <p className="text-zinc-500 text-sm mt-1">{t('seo.addKeywordToStart', 'Add a keyword above to get started')}</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-zinc-800">
                            <th className="text-left text-zinc-400 text-sm font-medium py-3 px-4">{t('seo.keyword', 'Keyword')}</th>
                            <th className="text-center text-zinc-400 text-sm font-medium py-3 px-4">{t('seo.rank', 'Rank')}</th>
                            <th className="text-left text-zinc-400 text-sm font-medium py-3 px-4">{t('seo.url', 'URL')}</th>
                            <th className="text-left text-zinc-400 text-sm font-medium py-3 px-4">{t('seo.lastCheck', 'Last Check')}</th>
                            <th className="text-right text-zinc-400 text-sm font-medium py-3 px-4">{t('seo.actions', 'Actions')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rankKeywords.map((kw) => (
                            <tr key={kw.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                              <td className="py-3 px-4">
                                <span className="text-white font-medium">{kw.keyword}</span>
                              </td>
                              <td className="py-3 px-4 text-center">
                                {kw.last_position === null || kw.last_position === undefined ? (
                                  kw.last_checked ? (
                                    <span className="text-zinc-500 text-sm">100+</span>
                                  ) : (
                                    <span className="text-zinc-600 text-sm">—</span>
                                  )
                                ) : (
                                  <span className={`inline-flex items-center justify-center w-10 h-10 rounded-lg font-bold text-lg ${
                                    kw.last_position <= 3 ? 'bg-emerald-500/20 text-emerald-400' :
                                    kw.last_position <= 10 ? 'bg-yellow-500/20 text-yellow-400' :
                                    kw.last_position <= 30 ? 'bg-orange-500/20 text-orange-400' :
                                    'bg-red-500/20 text-red-400'
                                  }`}>
                                    {kw.last_position}
                                  </span>
                                )}
                              </td>
                              <td className="py-3 px-4">
                                {kw.last_url ? (
                                  <a href={kw.last_url} target="_blank" rel="noopener noreferrer"
                                    className="text-emerald-400 hover:text-emerald-300 text-sm flex items-center gap-1 max-w-[300px] truncate">
                                    {kw.last_url.replace(/^https?:\/\//, '').substring(0, 50)}
                                    <ExternalLink className="w-3 h-3 flex-shrink-0" />
                                  </a>
                                ) : (
                                  <span className="text-zinc-600 text-sm">—</span>
                                )}
                              </td>
                              <td className="py-3 px-4">
                                {kw.last_checked ? (
                                  <span className="text-zinc-400 text-sm">
                                    {new Date(kw.last_checked).toLocaleDateString('en-US', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                ) : (
                                  <span className="text-zinc-600 text-sm">{t('seo.notCheckedYet', 'Not checked yet')}</span>
                                )}
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex items-center justify-end gap-2">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => checkKeywordRank(kw.id)}
                                    disabled={rankChecking === kw.id}
                                    className="text-zinc-400 hover:text-white"
                                    title={t('seo.checkRank', 'Check rank')}
                                  >
                                    {rankChecking === kw.id ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <RefreshCw className="w-4 h-4" />
                                    )}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => deleteRankKeyword(kw.id)}
                                    className="text-zinc-400 hover:text-red-400"
                                    title={t('common.delete', 'Delete')}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Info card */}
                <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
                  <h3 className="text-white font-medium mb-3">{t('seo.howItWorks', 'How It Works')}</h3>
                  <div className="space-y-2 text-zinc-400 text-sm">
                    <p>{t('seo.howStep1', '1. Enter your Google Custom Search API Key, CX ID, and site domain from the "Global Settings" tab.')}</p>
                    <p>{t('seo.howStep2', '2. Add the keywords you want to track.')}</p>
                    <p>{t('seo.howStep3', '3. Use "Check All" or individual check buttons to find out rankings.')}</p>
                    <p className="text-zinc-500 mt-2">{t('seo.howNote', 'Note: Google Custom Search API free plan offers 100 queries per day. Each keyword check uses up to 10 queries.')}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper Components
const NewRedirectForm = ({ onSubmit, onCancel }) => {
  const { t } = useTranslation();
  const [data, setData] = useState({ source_url: '', target_url: '', redirect_type: 301, is_active: true });
  
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-zinc-300">{t('seo.sourceUrl', 'Source URL')}</Label>
        <Input
          value={data.source_url}
          onChange={(e) => setData({ ...data, source_url: e.target.value })}
          className="bg-zinc-800 border-zinc-700 text-white"
          placeholder="/old-page"
        />
      </div>
      <div className="space-y-2">
        <Label className="text-zinc-300">{t('seo.targetUrl', 'Target URL')}</Label>
        <Input
          value={data.target_url}
          onChange={(e) => setData({ ...data, target_url: e.target.value })}
          className="bg-zinc-800 border-zinc-700 text-white"
          placeholder="/new-page"
        />
      </div>
      <div className="space-y-2">
        <Label className="text-zinc-300">{t('seo.redirectType', 'Redirect Type')}</Label>
        <Select value={String(data.redirect_type)} onValueChange={(v) => setData({ ...data, redirect_type: parseInt(v) })}>
          <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="301" className="text-zinc-300">301 ({t('seo.permanent', 'Permanent')})</SelectItem>
            <SelectItem value="302" className="text-zinc-300">302 ({t('seo.temporary', 'Temporary')})</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex justify-end gap-3 pt-4">
        <Button variant="outline" onClick={onCancel} className="border-zinc-700 text-zinc-300">{t('common.cancel', 'Cancel')}</Button>
        <Button onClick={() => onSubmit(data)} className="bg-emerald-600 hover:bg-emerald-700">{t('common.create', 'Create')}</Button>
      </div>
    </div>
  );
};

const TemplateForm = ({ template, onSave, onCancel }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(template);
  
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-zinc-300">{t('seo.templateName', 'Template Name')}</Label>
        <Input
          value={data.name}
          onChange={(e) => setData({ ...data, name: e.target.value })}
          className="bg-zinc-800 border-zinc-700 text-white"
        />
      </div>
      <div className="space-y-2">
        <Label className="text-zinc-300">{t('seo.pageType', 'Page Type')}</Label>
        <Select value={data.page_type} onValueChange={(v) => setData({ ...data, page_type: v })}>
          <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="listing" className="text-zinc-300">{t('seo.listing', 'Listing')}</SelectItem>
            <SelectItem value="category" className="text-zinc-300">{t('seo.category', 'Category')}</SelectItem>
            <SelectItem value="city" className="text-zinc-300">{t('seo.city', 'City')}</SelectItem>
            <SelectItem value="blog" className="text-zinc-300">{t('seo.blog', 'Blog')}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label className="text-zinc-300">{t('seo.titleTemplate', 'Title Template')}</Label>
        <Input
          value={data.title_template}
          onChange={(e) => setData({ ...data, title_template: e.target.value })}
          className="bg-zinc-800 border-zinc-700 text-white"
          placeholder="{name} - {category} | {site_name}"
        />
      </div>
      <div className="space-y-2">
        <Label className="text-zinc-300">{t('seo.descriptionTemplate', 'Description Template')}</Label>
        <Textarea
          value={data.description_template}
          onChange={(e) => setData({ ...data, description_template: e.target.value })}
          className="bg-zinc-800 border-zinc-700 text-white"
          rows={3}
        />
      </div>
      <div className="flex items-center gap-2">
        <Switch checked={data.is_default} onCheckedChange={(checked) => setData({ ...data, is_default: checked })} />
        <Label className="text-zinc-300">{t('seo.setAsDefault', 'Set as default for this page type')}</Label>
      </div>
      <div className="flex justify-end gap-3 pt-4">
        <Button variant="outline" onClick={onCancel} className="border-zinc-700 text-zinc-300">{t('common.cancel', 'Cancel')}</Button>
        <Button onClick={() => onSave(data)} className="bg-emerald-600 hover:bg-emerald-700">{t('common.save', 'Save')}</Button>
      </div>
    </div>
  );
};

const SchemaForm = ({ schema, onSave, onCancel }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(schema);
  const [jsonError, setJsonError] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const validateJson = (val) => {
    try {
      JSON.parse(val);
      setJsonError('');
      return true;
    } catch (e) {
      setJsonError(e.message);
      return false;
    }
  };

  const formatJson = () => {
    try {
      const parsed = JSON.parse(data.template);
      setData({ ...data, template: JSON.stringify(parsed, null, 2) });
      setJsonError('');
    } catch (e) {
      setJsonError(e.message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-zinc-300">{t('seo.schemaName', 'Schema Name')}</Label>
          <Input
            value={data.name}
            onChange={(e) => setData({ ...data, name: e.target.value })}
            className="bg-zinc-800 border-zinc-700 text-white"
            placeholder={t('seo.schemaNamePlaceholder', 'E.g.: Homepage Organization')}
          />
        </div>
        <div className="space-y-2">
          <Label className="text-zinc-300">{t('seo.schemaType', 'Schema Type')}</Label>
          <Select value={data.schema_type} onValueChange={(v) => setData({ ...data, schema_type: v })}>
            <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="Organization" className="text-zinc-300">{t('seo.organization', 'Organization')}</SelectItem>
              <SelectItem value="LocalBusiness" className="text-zinc-300">{t('seo.localBusiness', 'Local Business')}</SelectItem>
              <SelectItem value="WebSite" className="text-zinc-300">{t('seo.website', 'Website')}</SelectItem>
              <SelectItem value="Article" className="text-zinc-300">{t('seo.article', 'Article')}</SelectItem>
              <SelectItem value="FAQPage" className="text-zinc-300">{t('seo.faqPage', 'FAQ Page')}</SelectItem>
              <SelectItem value="HowTo" className="text-zinc-300">{t('seo.howTo', 'How To')}</SelectItem>
              <SelectItem value="Product" className="text-zinc-300">{t('seo.product', 'Product')}</SelectItem>
              <SelectItem value="Service" className="text-zinc-300">{t('seo.service', 'Service')}</SelectItem>
              <SelectItem value="Person" className="text-zinc-300">{t('seo.person', 'Person')}</SelectItem>
              <SelectItem value="BreadcrumbList" className="text-zinc-300">BreadcrumbList</SelectItem>
              <SelectItem value="Event" className="text-zinc-300">{t('seo.event', 'Event')}</SelectItem>
              <SelectItem value="Review" className="text-zinc-300">{t('seo.review', 'Review')}</SelectItem>
              <SelectItem value="VideoObject" className="text-zinc-300">{t('seo.video', 'Video')}</SelectItem>
              <SelectItem value="JobPosting" className="text-zinc-300">{t('seo.jobListing', 'Job Listing')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-zinc-300">{t('seo.jsonLdTemplate', 'JSON-LD Template')}</Label>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={formatJson} className="text-zinc-400 hover:text-white h-7 text-xs">
              {t('seo.format', 'Format')}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setShowPreview(!showPreview)} className="text-zinc-400 hover:text-white h-7 text-xs">
              {showPreview ? t('common.edit', 'Edit') : t('seo.preview', 'Preview')}
            </Button>
          </div>
        </div>

        {showPreview ? (
          <div className="bg-zinc-950 border border-zinc-700 rounded-lg p-4 min-h-[300px]">
            <div className="text-zinc-500 text-xs mb-2 font-mono">{'<script type="application/ld+json">'}</div>
            <pre className="text-emerald-400 text-sm font-mono whitespace-pre-wrap overflow-x-auto">
              {(() => { try { return JSON.stringify(JSON.parse(data.template), null, 2); } catch { return data.template; } })()}
            </pre>
            <div className="text-zinc-500 text-xs mt-2 font-mono">{'</script>'}</div>
          </div>
        ) : (
          <Textarea
            value={data.template}
            onChange={(e) => { setData({ ...data, template: e.target.value }); validateJson(e.target.value); }}
            className={`bg-zinc-800 border-zinc-700 text-white font-mono text-sm ${jsonError ? 'border-red-500/50' : ''}`}
            rows={14}
            placeholder='{"@context": "https://schema.org", "@type": "Organization", ...}'
          />
        )}

        {jsonError && (
          <p className="text-red-400 text-xs flex items-center gap-1">
            <span className="w-1 h-1 rounded-full bg-red-400" />
            {t('seo.jsonError', 'JSON Error')}: {jsonError}
          </p>
        )}
      </div>

      <div className="flex items-center justify-between pt-2">
        <div className="flex items-center gap-2">
          <Switch checked={data.is_active} onCheckedChange={(checked) => setData({ ...data, is_active: checked })} />
          <Label className="text-zinc-300">{t('seo.active', 'Active')}</Label>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={onCancel} className="border-zinc-700 text-zinc-300">{t('common.cancel', 'Cancel')}</Button>
          <Button onClick={() => onSave(data)} className="bg-emerald-600 hover:bg-emerald-700" disabled={!!jsonError}>{t('common.save', 'Save')}</Button>
        </div>
      </div>
    </div>
  );
};

export default SEOManagementPage;
